import { useMemo, useState } from 'react';

import type { RunEvent } from '../services/types';

import { Button } from './Button';

/**
 * Live event log for a single run.
 *
 * Consumes the `events` ring buffer produced by ``useRunEvents`` — which
 * is already subscribed to ``/ws/runs/:id/events`` for the active run
 * and records every payload the backend emits. We don't open a second
 * socket here; the parent component passes the cached events in.
 *
 * Display
 * -------
 * One row per event, newest at the bottom (chronological — the event
 * stream is small enough that scroll-follow isn't necessary for v0.1).
 * A short type badge, the event's ISO timestamp (local time), the
 * agent_id where applicable, and the message body.
 *
 * Filtering
 * ---------
 * A simple type filter (All / Log / Progress / HITL / Status). Matches
 * the shape of RunEvent without adding search or agent filters for
 * v0.1 — those are straightforward to layer on if reviewers hit the
 * noise ceiling (see Stage 5 polish in the queued tasks).
 */

type Filter = 'all' | RunEvent['type'];

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'log', label: 'Log' },
  { value: 'progress', label: 'Progress' },
  { value: 'run_status', label: 'Status' },
  { value: 'hitl_created', label: 'HITL' },
];

export interface LogsPanelProps {
  events: RunEvent[];
  /** WebSocket connection state — small indicator in the header. */
  connected?: boolean;
}

export function LogsPanel({ events, connected }: LogsPanelProps) {
  const [filter, setFilter] = useState<Filter>('all');

  const filtered = useMemo(() => {
    if (filter === 'all') return events;
    return events.filter((e) => e.type === filter);
  }, [events, filter]);

  const counts = useMemo(() => {
    const acc: Record<string, number> = { all: events.length };
    for (const e of events) {
      acc[e.type] = (acc[e.type] ?? 0) + 1;
    }
    return acc;
  }, [events]);

  return (
    <section aria-labelledby="logs-title" className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 id="logs-title" className="text-sm font-semibold">
          Event log{' '}
          <span className="text-muted font-normal">({events.length})</span>
        </h2>
        <span
          className={`text-xs font-medium inline-flex items-center gap-1.5 ${
            connected ? 'text-brand-dark' : 'text-muted'
          }`}
          title={
            connected
              ? 'WebSocket stream is connected'
              : 'WebSocket stream is not connected — events may be missing'
          }
        >
          <span
            aria-hidden
            className={`inline-block h-2 w-2 rounded-full ${
              connected ? 'bg-brand-dark' : 'bg-muted'
            }`}
          />
          {connected ? 'live' : 'disconnected'}
        </span>
      </div>

      <div className="flex gap-2 flex-wrap" role="group" aria-label="Filter by event type">
        {FILTERS.map(({ value, label }) => (
          <Button
            key={value}
            variant={filter === value ? 'primary' : 'ghost'}
            onClick={() => setFilter(value)}
          >
            {label} ({counts[value] ?? 0})
          </Button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-muted">
          {events.length === 0
            ? 'No events yet. The backend will push here as agents run.'
            : 'No events match the current filter.'}
        </p>
      ) : (
        <div
          className="overflow-auto rounded-md border border-border bg-paper"
          style={{ maxHeight: 520 }}
          role="region"
          aria-label="Event log table"
        >
          <table className="min-w-full text-xs">
            <thead className="sticky top-0 bg-surface-alt/60 backdrop-blur">
              <tr className="text-left font-medium text-muted uppercase tracking-wide">
                <th className="px-3 py-2 border-b border-border whitespace-nowrap">time</th>
                <th className="px-3 py-2 border-b border-border whitespace-nowrap">type</th>
                <th className="px-3 py-2 border-b border-border whitespace-nowrap">agent</th>
                <th className="px-3 py-2 border-b border-border">detail</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((evt, idx) => (
                <tr
                  key={`${evt.timestamp}-${idx}`}
                  className="odd:bg-surface-alt/20 align-top"
                >
                  <td className="px-3 py-1.5 border-b border-border/40 font-mono text-muted whitespace-nowrap">
                    {formatTime(evt.timestamp)}
                  </td>
                  <td className="px-3 py-1.5 border-b border-border/40">
                    <TypeBadge evt={evt} />
                  </td>
                  <td className="px-3 py-1.5 border-b border-border/40 font-mono">
                    {'agent_id' in evt ? evt.agent_id : '—'}
                  </td>
                  <td className="px-3 py-1.5 border-b border-border/40">
                    {renderDetail(evt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function TypeBadge({ evt }: { evt: RunEvent }) {
  let label: string;
  let tone: 'default' | 'warn' | 'danger' | 'success';
  switch (evt.type) {
    case 'log':
      label = 'log';
      tone = evt.status === 'error' ? 'danger' : evt.status === 'retry' ? 'warn' : 'default';
      break;
    case 'progress':
      label = 'progress';
      tone = 'default';
      break;
    case 'hitl_created':
      label = 'hitl';
      tone = evt.severity === 'high' ? 'danger' : evt.severity === 'medium' ? 'warn' : 'default';
      break;
    case 'run_status':
      label = 'status';
      tone = evt.status === 'error' ? 'danger' : evt.status === 'complete' ? 'success' : 'default';
      break;
  }
  const cls = {
    default: 'bg-surface-alt text-muted',
    warn: 'bg-warn/15 text-warn',
    danger: 'bg-danger/15 text-danger',
    success: 'bg-brand/15 text-brand-dark',
  }[tone];
  return (
    <span
      className={`inline-block rounded-sm px-1.5 py-0.5 text-[10px] font-semibold uppercase ${cls}`}
    >
      {label}
    </span>
  );
}

function renderDetail(evt: RunEvent): React.ReactNode {
  switch (evt.type) {
    case 'log':
      return (
        <span>
          {evt.status !== 'ok' && (
            <span className="font-mono text-muted mr-2">[{evt.status}]</span>
          )}
          {evt.message}
        </span>
      );
    case 'progress':
      return (
        <span className="font-mono text-muted">
          {evt.chunk_id}: {evt.percent}%
        </span>
      );
    case 'hitl_created':
      return (
        <span className="font-mono text-muted">
          {evt.hit_id} · severity={evt.severity}
        </span>
      );
    case 'run_status':
      return <span className="font-mono">run status → {evt.status}</span>;
  }
}

function formatTime(iso: string): string {
  // Best-effort HH:MM:SS.mmm local time. If parsing fails (unlikely — backend
  // emits ISO 8601), fall back to the raw string so information isn't lost.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number, w = 2) => String(n).padStart(w, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(
    d.getMilliseconds(),
    3,
  )}`;
}
